import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Results } from '../types/results';

type T = (key: string) => string;

const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const fmt = (n: number | undefined) => (n === undefined || n === null ? '-' : n.toFixed(1));

const duration = (s: number) => `${Math.floor(s / 60)}m ${s % 60}s`;

const barColor = (p: number) =>
  p >= 80 ? '#22c55e' : p >= 60 ? '#4EAEF5' : p >= 40 ? '#eab308' : '#ef4444';

const list = (items: string[]) =>
  `<ul>${items.map((i) => `<li><i class="dot"></i><span>${esc(i)}</span></li>`).join('')}</ul>`;

const LOCALES: Record<string, string> = { fr: 'fr-FR', en: 'en-GB', ar: 'ar' };

// Careers layout: rows (2 careers per row) that fit on a page below the banner
const ROWS_PER_PAGE = 13;

const ring = (value: number, max: number, label: string, sub: string) => {
  const R = 52;
  const C = 2 * Math.PI * R;
  const off = C * (1 - Math.min(Math.max(value / max, 0), 1));
  return `
  <div class="ring">
    <svg viewBox="0 0 130 130" width="150" height="150">
      <defs><linearGradient id="rg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#4EAEF5"/><stop offset="1" stop-color="#2563EB"/></linearGradient></defs>
      <circle cx="65" cy="65" r="${R}" fill="none" stroke="#e0ecfb" stroke-width="11"/>
      <circle cx="65" cy="65" r="${R}" fill="none" stroke="url(#rg)" stroke-width="11" stroke-linecap="round"
        stroke-dasharray="${C.toFixed(1)}" stroke-dashoffset="${off.toFixed(1)}" transform="rotate(-90 65 65)"/>
    </svg>
    <div class="ring-text"><b>${esc(label)}</b><small>${esc(sub)}</small></div>
  </div>`;
};

// SVG markup -> <img> (data URI): rasterises reliably in html2canvas, unlike inline <svg>
const svgImg = (svg: string, w: number, h: number, cls: string) =>
  `<img class="${cls}" width="${w}" height="${h}" alt="" src="data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}">`;

// Pill with text centred by SVG geometry (independent of font line-height metrics, so it renders the same everywhere)
const pill = (text: string, w: number, h: number, fs: number, fill: string) =>
  svgImg(`<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg"><rect width="${w}" height="${h}" rx="${h / 2}" fill="${fill}"/><text x="${w / 2}" y="${h / 2}" text-anchor="middle" dominant-baseline="central" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="${fs}" fill="#fff">${esc(text)}</text></svg>`, w, h, 'pill');

const avatar = (text: string, size: number) =>
  svgImg(`<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="avg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#2563EB"/><stop offset="1" stop-color="#4EAEF5"/></linearGradient></defs><circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="url(#avg)"/><text x="${size / 2}" y="${size / 2}" text-anchor="middle" dominant-baseline="central" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="${size * 0.38}" fill="#fff">${esc(text)}</text></svg>`, size, size, 'av');

const outlinePill = (text: string) => {
  const w = Math.round(text.length * 7.4 + 44);
  const h = 28;
  return svgImg(`<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg"><rect x="0.75" y="0.75" width="${w - 1.5}" height="${h - 1.5}" rx="${h / 2}" fill="#ffffff" fill-opacity="0.16" stroke="#ffffff" stroke-opacity="0.6" stroke-width="1.5"/><text x="${w / 2}" y="${h / 2}" text-anchor="middle" dominant-baseline="central" font-family="Arial, Helvetica, sans-serif" font-size="13" fill="#fff">${esc(text)}</text></svg>`, w, h, 'vpill');
};

const COLORS: Record<string, string> = { g: '#16a34a', b: '#2563EB', n: '#64748b' };

type Career = Results['career_recommendations'][number];
type Group = { title: string; cls: string; items: Career[] };

const buildHtml = (r: Results, t: T, isRTL: boolean, logoUrl: string, uiLang: string): string => {
  const pct = Math.round((r.scores.raw_score / r.scores.max_score) * 100);
  const correct = r.answers.filter((a) => a.is_correct).length;
  const wrong = r.answers.length - correct;
  const tempo = r.profile.tempo_label || r.profile.tempo;
  const locale = LOCALES[uiLang] || 'fr-FR';
  const date = new Date(r.started_at).toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' });
  const finalPct = r.scores.final_score_percentage || Math.round((r.scores.final_score / 40) * 100);
  const testLabel = `${t('results.version')} ${r.test_version} · ${t(`languages.${r.language}`) || r.language}`;

  const cand = r.candidate || {};
  const opt = (cat: string, v?: string | null) => (v ? t(`options.${cat}.${v}`) : '');
  const infoRows: [string, string][] = [
    [t('auth.age'), cand.age ? String(cand.age) : ''],
    [t('auth.sex'), opt('sex', cand.sex)],
    [t('auth.nativeLanguage'), opt('nativeLanguage', cand.native_language)],
    [t('auth.englishLevel'), opt('englishLevel', cand.english_level)],
    [t('auth.educationLevel'), opt('educationLevel', cand.education_level)],
    [t('auth.studySpecialty'), opt('studySpecialty', cand.study_specialty)],
    [t('auth.employmentStatus'), opt('employmentStatus', cand.employment_status)],
    [t('auth.workSector'), opt('workSector', cand.work_sector)],
  ];
  const infoHtml = infoRows
    .filter(([, v]) => v)
    .map(([k, v]) => `<div class="irow"><span>${esc(k)}</span><b>${esc(v)}</b></div>`)
    .join('');
  const initials = (cand.full_name || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  // Score adjustments (only the non-zero ones)
  const adj = r.scores.score_adjustments;
  const adjDefs: [keyof typeof adj, string, boolean][] = [
    ['bonus_etudes', 'bonusEducation', true], ['bonus_etudes_ar', 'bonusEducation', true],
    ['bonus_domaine', 'bonusDomain', true], ['bonus_metier', 'bonusProfession', true],
    ['bonus_arabophone', 'bonusArabophone', true], ['bonus_exposition', 'bonusExposure', true],
    ['malus_age', 'malusAge', false], ['malus_niveau', 'malusLevel', false],
  ];
  const adjRows = adjDefs
    .filter(([k]) => adj[k])
    .map(([k, label, pos]) => `<div class="row"><span>${esc(t(`results.${label}`))}</span><b style="color:${pos ? '#16a34a' : '#dc2626'}">${pos ? '+' : ''}${adj[k]}</b></div>`)
    .join('');

  const dims = Object.entries(r.dimension_scores)
    .map(
      ([k, v]) => `
      <div class="dim">
        <div class="row"><span>${esc(t(`results.section.${k}`))}</span><b>${v.score}/${v.max} · ${v.percentage}%</b></div>
        <div class="bar"><div style="width:${v.percentage}%;background:${barColor(v.percentage)}"></div></div>
      </div>`
    )
    .join('');

  // ---- Careers: grouped, 2 per row, paginated ----
  const recs = r.career_recommendations;
  const groups: Group[] = [
    { title: t('results.excellentMatch'), cls: 'g', items: recs.filter((c) => c.match_percentage >= 80) },
    { title: t('results.goodMatch'), cls: 'b', items: recs.filter((c) => c.match_percentage >= 60 && c.match_percentage < 80) },
    { title: t('results.moderateMatch'), cls: 'n', items: recs.filter((c) => c.match_percentage < 60) },
  ].filter((g) => g.items.length);

  const careerCard = (c: Career, cls: string) => `
    <div class="career">
      <div class="ct"><b>${esc(c.job_title)}</b><small>${esc(c.sector)}</small>
        <div class="bar slim"><div style="width:${c.match_percentage}%"></div></div></div>
      ${pill(`${c.match_percentage}%`, 48, 26, 13, COLORS[cls])}
    </div>`;

  const careerPages: string[] = [];
  let cur = '';
  let used = 1; // the section title takes one row on the first page
  const flush = () => {
    if (cur) careerPages.push(cur);
    cur = '';
    used = 0;
  };
  groups.forEach((g) => {
    let i = 0;
    while (i < g.items.length) {
      if (ROWS_PER_PAGE - used < 2) flush(); // need header + at least one row
      const rowsLeft = ROWS_PER_PAGE - used - 1; // 1 row for the header
      const take = Math.min(g.items.length - i, rowsLeft * 2);
      const chunk = g.items.slice(i, i + take);
      cur += `<div class="grp ${g.cls}"><h3>${esc(g.title)} ${pill(String(g.items.length), 28, 20, 11, COLORS[g.cls])}</h3><div class="cgrid">${chunk.map((c) => careerCard(c, g.cls)).join('')}</div></div>`;
      used += 1 + Math.ceil(take / 2);
      i += take;
    }
  });
  const note = `<div class="box amber">${esc(t('results.recommendationsInfo'))}</div>`;
  if (ROWS_PER_PAGE - used < 1) flush();
  cur += note;
  flush();

  // ---- Page shell ----
  const banner = `
  <div class="banner">
    <div class="logo"><img src="${esc(logoUrl)}" alt="Wordflow"></div>
    <div><h1>Wordflow</h1><p>${esc(t('results.title'))} · ${esc(cand.full_name || '')}</p></div>
    <div class="tag"><b>${esc(date)}</b>${esc(testLabel)}</div>
  </div>`;
  const page = (label: string, inner: string) => `
<div class="page">
  ${banner}
  <div class="body">${inner}</div>
  <footer><span>Wordflow · ${esc(cand.full_name || '')}</span><span>${esc(label)} · <bdi dir="ltr">__PN__ / __PT__</bdi></span></footer>
</div>`;
  const title = (txt: string) => `<div class="title"><i></i><h2>${esc(txt)}</h2></div>`;

  const summary = title(t('results.tabs.summary')) + `
    <div class="top">
      ${ring(r.scores.final_score, 40, `${fmt(r.scores.final_score)}/40`, `${finalPct}%`)}
      <div class="meta">
        <div class="row"><span>${esc(t('results.percentile'))}</span><b>${r.scores.percentile}%</b></div>
        <div class="row"><span>${esc(t('results.profile'))}</span><b>${esc(r.profile.profile_name)}</b></div>
        <div class="row"><span>${esc(t('results.level'))}</span><b>${esc(r.profile.level)}</b></div>
        <div class="row"><span>${esc(t('results.pace'))}</span><b>${esc(tempo)}</b></div>
        <div class="row"><span>${esc(t('results.totalDuration'))}</span><b>${duration(r.total_duration_seconds)}</b></div>
      </div>
    </div>
    <div class="grid">
      <div class="card"><small>${esc(t('results.rawScore'))}</small><b>${r.scores.raw_score}/${r.scores.max_score}</b></div>
      <div class="card"><small>${esc(t('results.adjustedScore'))}</small><b>${fmt(r.scores.adjusted_score)}</b></div>
      <div class="card"><small>${esc(t('results.speedScore'))}</small><b>${fmt(r.scores.speed_score)}</b></div>
      <div class="card"><small>${esc(t('results.finalScore'))}</small><b>${fmt(r.scores.final_score)}</b></div>
    </div>
    <div class="stats">
      <div class="stat"><b>${r.answers.length}</b><small>${esc(t('results.totalQuestions'))}</small></div>
      <div class="stat ok"><b>${correct}</b><small>${esc(t('results.correct'))}</small></div>
      <div class="stat ko"><b>${wrong}</b><small>${esc(t('results.incorrect'))}</small></div>
    </div>
    <h3 class="sub">${esc(t('results.performanceBySection'))}</h3>
    ${dims}
    <div class="two">
      <div>
        <h3 class="sub">${esc(t('results.session'))}</h3>
        <div class="box">
          <div class="row"><span>${esc(t('results.language'))}</span><b>${esc(t(`languages.${r.language}`) || r.language)}</b></div>
          <div class="row"><span>${esc(t('results.version'))}</span><b>${esc(r.test_version)}</b></div>
          <div class="row"><span>${esc(t('results.avgTimePerQuestion'))}</span><b>${Math.round(r.total_duration_seconds / Math.max(r.answers.length, 1))}s</b></div>
        </div>
      </div>
      ${adjRows ? `<div>
        <h3 class="sub">${esc(t('results.adjustmentsDetail'))}</h3>
        <div class="box">${adjRows}
          <div class="row"><span><b>${esc(t('results.totalAdjustment'))}</b></span><b>${adj.total_adjustment >= 0 ? '+' : ''}${adj.total_adjustment}</b></div>
        </div>
      </div>` : ''}
    </div>`;

  const pctl = Math.min(Math.max(r.scores.percentile, 0), 100);
  const profile = title(t('results.tabs.profile')) + `
    <div class="hero">
      <div>
        <small class="lbl">${esc(t('results.yourProfile'))}</small>
        <h3>${esc(r.profile.emoji)} ${esc(r.profile.level)} ${esc(tempo)}</h3>
        <small>${esc(t('results.profileNum'))} ${r.profile.profile_number} • ${esc(r.profile.profile_name)}</small>
      </div>
      <div class="big">${pct}%<small>${esc(t('results.successRate'))}</small></div>
    </div>
    <div class="facts">
      <div class="fact"><small>${esc(t('results.scoreRange'))}</small><b>${esc(r.profile.score_range)}</b></div>
      <div class="fact"><small>${esc(t('results.percentile'))}</small><b>${r.scores.percentile}%</b></div>
      ${r.profile.percentile_level ? `<div class="fact"><small>${esc(t('results.level'))}</small><b>${esc(r.profile.percentile_level)}</b></div>` : ''}
      ${r.profile.frequency ? `<div class="fact"><small>Fréquence</small><b>${esc(r.profile.frequency)}</b></div>` : ''}
    </div>
    <div class="gauge">
      <div class="gtrack"><div class="gfill" style="width:${pctl}%"></div><div class="gpin" style="inset-inline-start:${pctl}%"></div></div>
      <div class="gl"><span>0</span><span>25</span><span>50</span><span>75</span><span>100</span></div>
    </div>
    <div class="box"><h4>${esc(t('results.conclusion'))}</h4>${esc(r.profile.conclusion)}</div>
    ${r.profile.interpretation ? `<div class="box blue"><h4>${esc(t('results.profile'))}</h4>${esc(r.profile.interpretation)}</div>` : ''}
    ${r.profile.action ? `<div class="box blue"><h4>${esc(t('results.actionPlan'))}</h4>${esc(r.profile.action)}</div>` : ''}
    ${r.dimension_analysis.strengths.length ? `<div class="box green"><h4>${esc(t('results.strengths'))}</h4>${list(r.dimension_analysis.strengths)}</div>` : ''}
    ${r.dimension_analysis.areas_for_improvement.length ? `<div class="box amber"><h4>${esc(t('results.improvementAreas'))}</h4>${list(r.dimension_analysis.areas_for_improvement)}</div>` : ''}`;

  const careerHead = title(t('results.careerRecommendations'));
  const pages = [
    page(t('results.tabs.summary'), summary),
    page(t('results.tabs.profile'), profile),
    ...careerPages.map((c, i) => page(t('results.tabs.careers'), (i === 0 ? careerHead : '') + c)),
  ];
  const total = pages.length + 1;
  const numbered = pages.map((p, i) => p.replace('__PN__', String(i + 2)).replace('__PT__', String(total))).join('\n');

  return `<!doctype html>
<html dir="${isRTL ? 'rtl' : 'ltr'}">
<head>
<meta charset="utf-8">
<title>Wordflow - ${esc(t('results.title'))} #${r.attempt_id}</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; }
  body { font-family: 'Segoe UI', 'Helvetica Neue', Tahoma, Arial, sans-serif; color: #1f2937; font-size: 13.5px; line-height: 1.55; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { width: 210mm; height: 297mm; overflow: hidden; position: relative; break-after: page; }
  .body { padding: 0 15mm; }

  .banner { background: linear-gradient(120deg, #1e40af 0%, #2563EB 55%, #4EAEF5 100%); color: #fff; padding: 9mm 15mm 8mm; margin-bottom: 9mm; display: flex; align-items: center; gap: 16px; }
  .banner .logo { width: 58px; height: 58px; background: #fff; border-radius: 15px; padding: 5px; flex: none; }
  .banner .logo img { width: 100%; height: 100%; }
  .banner h1 { margin: 0; font-size: 24px; letter-spacing: .5px; line-height: 1.2; }
  .banner p { margin: 2px 0 0; color: #dbeafe; font-size: 12px; }
  .banner .tag { margin-inline-start: auto; text-align: end; font-size: 11px; color: #dbeafe; }
  .banner .tag b { display: block; color: #fff; font-size: 14px; }

  .title { display: flex; align-items: center; gap: 10px; margin: 0 0 14px; }
  .title i { width: 5px; height: 24px; border-radius: 3px; background: linear-gradient(#4EAEF5, #2563EB); }
  .title h2 { margin: 0; font-size: 20px; color: #1e3a8a; }
  h3.sub { font-size: 14px; color: #1e3a8a; margin: 20px 0 10px; }

  .top { display: flex; align-items: center; gap: 22px; background: #f3f8fe; border: 1px solid #dbeafe; border-radius: 14px; padding: 16px 22px; margin-bottom: 14px; }
  .ring { position: relative; width: 150px; height: 150px; flex: none; }
  .ring-text { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
  .ring-text b { font-size: 26px; color: #1e3a8a; }
  .ring-text small { color: #6b7280; font-size: 12px; }
  .meta { flex: 1; }
  .row { display: flex; justify-content: space-between; gap: 10px; padding: 5px 0; border-bottom: 1px dashed #d6e4f7; }
  .row:last-child { border-bottom: 0; }
  .row span { color: #6b7280; }

  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
  .card { background: #fff; border: 1px solid #e5e7eb; border-top: 4px solid #4EAEF5; border-radius: 10px; padding: 11px 6px; text-align: center; }
  .card:nth-child(2) { border-top-color: #2563EB; } .card:nth-child(3) { border-top-color: #f59e0b; } .card:nth-child(4) { border-top-color: #22c55e; }
  .card small { display: block; color: #6b7280; font-size: 11.5px; }
  .card b { font-size: 20px; color: #1e3a8a; }

  .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .stat { border-radius: 10px; padding: 10px; text-align: center; background: #f3f4f6; }
  .stat b { display: block; font-size: 22px; }
  .stat small { color: #6b7280; }
  .stat.ok { background: #f0fdf4; color: #15803d; } .stat.ko { background: #fef2f2; color: #b91c1c; }

  .two { display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; gap: 14px; align-items: start; }
  .two .box { margin-bottom: 0; }
  .dim { margin-bottom: 10px; }
  .dim .row { border: 0; padding: 0 0 4px; } .dim .row span { color: #374151; font-weight: 600; }
  .bar { height: 10px; background: #e8eef6; border-radius: 5px; overflow: hidden; }
  .bar div { height: 100%; border-radius: 5px; }
  .bar.slim { height: 5px; margin-top: 5px; }

  .hero { background: linear-gradient(135deg, #2563EB, #4EAEF5); color: #fff; border-radius: 16px; padding: 20px 24px; margin-bottom: 14px; display: flex; justify-content: space-between; align-items: center; }
  .hero small { color: #e0f0ff; font-size: 12px; }
  .hero .lbl { text-transform: uppercase; letter-spacing: 1px; }
  .hero h3 { margin: 6px 0; font-size: 26px; line-height: 1.2; }
  .hero .big { text-align: center; background: rgba(255,255,255,.18); border-radius: 12px; padding: 12px 20px; font-size: 32px; font-weight: 700; line-height: 1.2; }
  .hero .big small { display: block; font-size: 11px; font-weight: 400; }

  .facts { display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; gap: 10px; margin-bottom: 14px; }
  .fact { background: #f3f8fe; border: 1px solid #dbeafe; border-radius: 10px; padding: 9px 6px; text-align: center; }
  .fact small { display: block; color: #6b7280; font-size: 11px; } .fact b { color: #1e3a8a; font-size: 15px; }
  .gauge { margin: 4px 0 18px; }
  .gtrack { position: relative; height: 12px; border-radius: 6px; background: linear-gradient(90deg, #fecaca, #fde68a, #bbf7d0); }
  .gfill { display: none; }
  .gpin { position: absolute; top: -4px; width: 20px; height: 20px; margin-inline-start: -10px; border-radius: 50%; background: #2563EB; border: 3px solid #fff; }
  .gl { display: flex; justify-content: space-between; color: #9ca3af; font-size: 10.5px; margin-top: 5px; }

  .box { border: 1px solid #e5e7eb; border-inline-start: 5px solid #94a3b8; background: #f9fafb; border-radius: 10px; padding: 12px 16px; margin-bottom: 12px; }
  .box h4 { margin: 0 0 5px; font-size: 14px; }
  .box.blue { background: #eff6ff; border-color: #bfdbfe; border-inline-start-color: #2563EB; } .box.blue h4 { color: #1e40af; }
  .box.green { background: #f0fdf4; border-color: #bbf7d0; border-inline-start-color: #22c55e; } .box.green h4 { color: #166534; }
  .box.amber { background: #fffbeb; border-color: #fde68a; border-inline-start-color: #f59e0b; } .box.amber h4 { color: #92400e; }
  ul { margin: 0; padding: 0; list-style: none; }
  li { display: flex; align-items: flex-start; gap: 10px; margin: 4px 0; line-height: 21px; }
  li .dot { flex: none; width: 6px; height: 6px; border-radius: 50%; background: #475569; margin-top: 8px; }
  .box.green .dot { background: #16a34a; } .box.amber .dot { background: #d97706; }

  .grp { margin-bottom: 8px; }
  .grp h3 { font-size: 14px; margin: 0 0 8px; height: 22px; display: flex; align-items: center; gap: 8px; }
  .grp h3 span { display: block; font-size: 11px; line-height: 18px; height: 18px; color: #fff; border-radius: 9px; padding: 0 9px; }
  .grp.g h3 { color: #16a34a; } .grp.b h3 { color: #2563EB; } .grp.n h3 { color: #64748b; }
  .grp.g h3 span { background: #16a34a; } .grp.b h3 span { background: #2563EB; } .grp.n h3 span { background: #64748b; }
  .cgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .career { display: flex; align-items: center; gap: 10px; border: 1px solid #e5e7eb; border-inline-start: 4px solid; border-radius: 10px; padding: 7px 10px; height: 60px; background: #fff; }
  .grp.g .career { border-inline-start-color: #16a34a; } .grp.b .career { border-inline-start-color: #2563EB; } .grp.n .career { border-inline-start-color: #94a3b8; }
  .ct { flex: 1; min-width: 0; } .ct b { display: block; font-size: 12.5px; line-height: 1.25; } .ct small { display: block; color: #6b7280; font-size: 11px; }
  .grp.g .bar.slim div { background: #22c55e; } .grp.b .bar.slim div { background: #2563EB; } .grp.n .bar.slim div { background: #94a3b8; }
  .pill { display: block; flex: none; }
  .badge { display: block; font-weight: 700; font-size: 12.5px; line-height: 24px; height: 24px; color: #fff; border-radius: 12px; padding: 0 9px; text-align: center; }
  .grp.g .badge { background: #16a34a; } .grp.b .badge { background: #2563EB; } .grp.n .badge { background: #64748b; }

  footer { position: absolute; bottom: 8mm; left: 15mm; right: 15mm; display: flex; justify-content: space-between; color: #9ca3af; font-size: 10.5px; border-top: 1px solid #e5e7eb; padding-top: 6px; }

  .cover .hd { background: linear-gradient(150deg, #1e3a8a 0%, #2563EB 55%, #4EAEF5 100%); color: #fff; height: 175mm; padding: 24mm 20mm 0; text-align: center; border-bottom-left-radius: 50% 14mm; border-bottom-right-radius: 50% 14mm; }
  .cover .hd .logo { width: 38mm; height: 38mm; background: #fff; border-radius: 50%; padding: 4mm; margin: 0 auto 9mm; }
  .cover .hd .logo img { width: 100%; height: 100%; }
  .cover .hd h1 { margin: 0; font-size: 46px; letter-spacing: 2px; line-height: 1.2; }
  .cover .hd .kind { margin: 8px 0 0; font-size: 17px; color: #dbeafe; text-transform: uppercase; letter-spacing: 3px; }
  .cover .hd .ver { margin-top: 8mm; } .vpill { display: block; margin: 0 auto; }
  .cand { margin: -46mm 18mm 0; position: relative; background: #fff; border-radius: 16px; padding: 9mm 11mm 6mm; border: 2px solid #bfdbfe; }
  .cand .who { display: flex; align-items: center; gap: 16px; padding-bottom: 6mm; margin-bottom: 3mm; border-bottom: 2px solid #e8eef6; }
  .cand .av { flex: none; display: block; }
  .cand .who h2 { margin: 0; font-size: 25px; color: #1e3a8a; line-height: 1.25; }
  .cand .who small { color: #6b7280; font-size: 12.5px; }
  .infos { display: grid; grid-template-columns: 1fr 1fr; gap: 0 10mm; }
  .irow { display: flex; flex-direction: column; padding: 7px 0; border-bottom: 1px dashed #d6e4f7; }
  .irow span { color: #6b7280; font-size: 10.5px; text-transform: uppercase; letter-spacing: .5px; }
  .irow b { font-size: 13.5px; color: #1f2937; }
  .strip { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 10mm 18mm 0; }
  .strip div { text-align: center; background: #f3f8fe; border: 1px solid #dbeafe; border-radius: 12px; padding: 10px 6px; }
  .strip small { display: block; color: #6b7280; font-size: 11px; text-transform: uppercase; letter-spacing: .5px; }
  .strip b { font-size: 20px; color: #1e3a8a; }
  .cover .when { text-align: center; margin-top: 12mm; color: #6b7280; }
  .cover .when b { color: #1e3a8a; }
</style>
</head>
<body>

<div class="page cover">
  <div class="hd">
    <div class="logo"><img id="logo" src="${esc(logoUrl)}" alt="Wordflow"></div>
    <h1>Wordflow</h1>
    <p class="kind">${esc(t('results.title'))}</p>
    <div class="ver">${outlinePill(testLabel)}</div>
  </div>
  <div class="cand">
    <div class="who">
      ${avatar(initials, 64)}
      <div><h2>${esc(cand.full_name || '')}</h2><small>${esc(cand.email || '')}</small></div>
    </div>
    <div class="infos">${infoHtml}</div>
  </div>
  <div class="strip">
    <div><small>${esc(t('results.finalScore'))}</small><b>${fmt(r.scores.final_score)}/40</b></div>
    <div><small>${esc(t('results.percentile'))}</small><b>${r.scores.percentile}%</b></div>
    <div><small>${esc(t('results.profile'))}</small><b>${esc(r.profile.emoji)}</b></div>
  </div>
  <div class="when">${esc(t('results.date'))} : <b>${esc(date)}</b></div>
  <footer><span>Wordflow by Scaleo</span><span><bdi dir="ltr">1 / ${total}</bdi></span></footer>
</div>
${numbered}
</body>
</html>`;
};

/** Renders each page of the report to an image and saves a real PDF file (no print dialog). */
export const downloadResultsPdf = async (r: Results, t: T, isRTL: boolean, uiLang: string = 'fr'): Promise<void> => {
  const logoUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}/logo.svg`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:794px;height:1123px;border:0;';
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(buildHtml(r, t, isRTL, logoUrl, uiLang));
    doc.close();

    await Promise.all(
      Array.from(doc.images).map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise<void>((res) => {
                img.onload = () => res();
                img.onerror = () => res();
              })
      )
    );
    if (doc.fonts?.ready) await doc.fonts.ready;

    // Safety net: if a page's content is taller than the space above the footer, scale it down to fit
    doc.querySelectorAll<HTMLElement>('.page').forEach((pg) => {
      const body = pg.querySelector<HTMLElement>('.body');
      const foot = pg.querySelector<HTMLElement>('footer');
      if (!body || !foot) return;
      const room = foot.offsetTop - body.offsetTop - 10;
      if (body.scrollHeight > room) {
        body.style.transformOrigin = 'top center';
        body.style.transform = `scale(${room / body.scrollHeight})`;
      }
    });

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pages = Array.from(doc.querySelectorAll<HTMLElement>('.page'));
    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: 794,
      });
      if (i > 0) pdf.addPage();
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.98), 'JPEG', 0, 0, 210, 297, undefined, 'SLOW');
    }

    const name = (r.candidate?.full_name || `attempt-${r.attempt_id}`).replace(/[^\w\-]+/g, '_');
    pdf.save(`Wordflow_${name}_${r.attempt_id}.pdf`);
  } finally {
    document.body.removeChild(iframe);
  }
};
